import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import { fromCents } from '@ankor-io/common/domain/pay/currency'
import { ReconciledTransferPayload, TransferReconcileStatus } from '@ankor-io/common/types/transaction'
import {
  Badge,
  Button,
  CopyToClipboard,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  Label,
  Loading,
  Switch,
  SwitchField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ankor-io/ui-components'

import {
  useReconcileConnectedAccountTransferStatusMutation,
  useReconcileConnectedAccountTransfersQuery,
} from '@/queries'

const STATUS_BADGE: Record<TransferReconcileStatus, { color: 'amber' | 'green' | 'red'; label: string }> = {
  [TransferReconcileStatus.NO_WEBHOOK_LOG]: { color: 'amber', label: 'No webhook log' },
  [TransferReconcileStatus.IN_SYNC]: { color: 'green', label: 'In sync' },
  [TransferReconcileStatus.STATUS_MISMATCH]: { color: 'red', label: 'Status mismatch' },
}

const STATUS_SORT: Record<TransferReconcileStatus, number> = {
  [TransferReconcileStatus.STATUS_MISMATCH]: 0,
  [TransferReconcileStatus.NO_WEBHOOK_LOG]: 1,
  [TransferReconcileStatus.IN_SYNC]: 2,
}

const SHORT_ID_LENGTH = 8

const shortId = (id: string) => (id.length > SHORT_ID_LENGTH ? `${id.slice(0, SHORT_ID_LENGTH)}…` : id)

export function ConnectedAccountTransfersReconcile({
  connectedAccountId,
  orgId,
  vesselId,
}: {
  connectedAccountId: string
  orgId: string
  vesselId: string
}) {
  const { data, isFetching, isError, error, refetch } = useReconcileConnectedAccountTransfersQuery(
    orgId,
    vesselId,
    connectedAccountId,
  )
  const applyStatus = useReconcileConnectedAccountTransferStatusMutation()
  const [applyRow, setApplyRow] = useState<ReconciledTransferPayload | null>(null)
  const [hideInSync, setHideInSync] = useState(true)

  const rows = useMemo(
    () => (data ? [...data].sort((a, b) => STATUS_SORT[a.status] - STATUS_SORT[b.status]) : []),
    [data],
  )

  const counts = useMemo(() => {
    const result: Record<TransferReconcileStatus, number> = {
      [TransferReconcileStatus.STATUS_MISMATCH]: 0,
      [TransferReconcileStatus.NO_WEBHOOK_LOG]: 0,
      [TransferReconcileStatus.IN_SYNC]: 0,
    }
    rows.forEach((row) => {
      result[row.status] += 1
    })
    return result
  }, [rows])

  const visibleRows = hideInSync ? rows.filter((row) => row.status !== TransferReconcileStatus.IN_SYNC) : rows

  const openApply = (row: ReconciledTransferPayload) => {
    applyStatus.reset()
    setApplyRow(row)
  }

  const closeApply = () => {
    if (applyStatus.isPending) {
      return
    }
    setApplyRow(null)
  }

  const handleApply = () => {
    if (!applyRow) {
      return
    }
    const runApply = async () => {
      await applyStatus.mutateAsync({ orgId, vesselId, connectedAccountId, transferId: applyRow.transferId })
      setApplyRow(null)
      // Compare again so the fixed row flips to "In sync".
      refetch()
    }
    toast.promise(runApply, {
      loading: 'Applying log status…',
      success: 'Stored status updated from the webhook log.',
      error: 'Failed to apply the log status.',
    })
  }

  return (
    <div className="mt-8">
      <h4 className="font-bold text-sm">Reconcile transfers</h4>
      <p className="text-sm text-gray-500 mt-0.5 dark:text-gray-400">
        Compares the stored AnkorPay status of the most recent transfers (up to 500) with the status from their webhook
        log.
      </p>
      <p className="text-sm text-gray-500 mt-0.5 dark:text-gray-400">
        Comparing is read-only and changes nothing. Fixes are applied one row at a time with{' '}
        <span className="font-medium text-gray-700 dark:text-gray-300">Apply log status</span>.
      </p>

      <div className="mt-3">
        {data ? (
          <Button
            plain
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Compare again
          </Button>
        ) : (
          <Button
            color="primary"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Compare statuses
          </Button>
        )}
      </div>

      {isFetching && (
        <div
          role="status"
          className="flex items-center gap-2 mt-3 text-sm text-gray-500"
        >
          <Loading />
          <span>Comparing statuses…</span>
        </div>
      )}

      {isError && !isFetching && (
        <p
          role="alert"
          className="mt-3 text-sm text-red-600"
        >
          The comparison failed. {error instanceof Error ? error.message : ''} Nothing was changed. Try again.
        </p>
      )}

      {!isFetching && !isError && data?.length === 0 && (
        <p className="mt-3 text-sm text-gray-500">No AnkorPay transfers found for this connected account.</p>
      )}

      {!isFetching && !isError && rows.length > 0 && (
        <>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
            <div
              className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
              data-testid="transfers-reconcile-summary"
            >
              <span>
                {rows.length} {rows.length === 1 ? 'transfer' : 'transfers'} checked:
              </span>
              <Badge color="red">{counts[TransferReconcileStatus.STATUS_MISMATCH]} mismatched</Badge>
              <Badge color="amber">{counts[TransferReconcileStatus.NO_WEBHOOK_LOG]} no webhook log</Badge>
              <Badge color="green">{counts[TransferReconcileStatus.IN_SYNC]} in sync</Badge>
            </div>
            <SwitchField className="!grid-cols-[auto_auto] !gap-x-3">
              <Label className="text-sm">Hide in-sync rows</Label>
              <Switch
                color="dark/gray"
                checked={hideInSync}
                onChange={setHideInSync}
              />
            </SwitchField>
          </div>

          {visibleRows.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">All checked transfers are in sync. Nothing to apply.</p>
          ) : (
            <div className="overflow-x-auto mt-3">
              <Table className="max-w-[calc(100vw-2rem)] lg:max-w-[calc(100vw-20rem)]">
                <TableHead>
                  <TableRow>
                    <TableHeader>Result</TableHeader>
                    <TableHeader>AnkorPay status</TableHeader>
                    <TableHeader>Webhook log status</TableHeader>
                    <TableHeader>Action</TableHeader>
                    <TableHeader>Transfer</TableHeader>
                    <TableHeader>Amount</TableHeader>
                    <TableHeader>Created</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row) => {
                    const isMismatch = row.status === TransferReconcileStatus.STATUS_MISMATCH
                    const isApplying = applyStatus.isPending && applyRow?.transferId === row.transferId
                    return (
                      <TableRow key={row.transferId}>
                        <TableCell>
                          <Badge color={STATUS_BADGE[row.status].color}>{STATUS_BADGE[row.status].label}</Badge>
                        </TableCell>
                        <TableCell
                          className={`text-sm ${isMismatch ? 'font-medium text-red-700 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}
                        >
                          {row.dbStatus}
                        </TableCell>
                        <TableCell
                          className={`text-sm text-gray-900 dark:text-gray-100 ${isMismatch ? 'font-medium' : ''}`}
                        >
                          {row.webhookLogStatus ?? '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {isMismatch && (
                            <Button
                              outline
                              className="disabled:cursor-not-allowed"
                              onClick={() => openApply(row)}
                              disabled={applyStatus.isPending}
                            >
                              {isApplying ? 'Applying…' : 'Apply log status'}
                            </Button>
                          )}
                          {row.status === TransferReconcileStatus.NO_WEBHOOK_LOG && (
                            <span className="text-gray-500 dark:text-gray-400">No log to apply</span>
                          )}
                          {row.status === TransferReconcileStatus.IN_SYNC && (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div
                            className="max-w-56 truncate text-gray-900 dark:text-gray-100"
                            title={row.reference || undefined}
                          >
                            {row.reference || '-'}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <span
                              className="font-mono"
                              title={row.transferId}
                            >
                              {shortId(row.transferId)}
                            </span>
                            <CopyToClipboard
                              text={row.transferId}
                              tooltip="Copy transfer ID"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                          {fromCents(row.transferAmountCents).toFixed(2)} {row.transferCurrencyCode}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                          {row.createdAt.slice(0, 10)}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}

      <Dialog
        open={applyRow !== null}
        onClose={closeApply}
        size="md"
      >
        <DialogTitle>Apply webhook log status</DialogTitle>
        <DialogDescription>
          This overwrites the status AnkorPay stores for this transfer with the status from its webhook log. Nothing is
          sent to Airwallex.
        </DialogDescription>
        {applyRow && (
          <DialogBody>
            <DescriptionList>
              <DescriptionTerm>Transfer</DescriptionTerm>
              <DescriptionDetails>
                <div>{applyRow.reference || '-'}</div>
                <div className="font-mono text-xs text-gray-500 break-all dark:text-gray-400">
                  {applyRow.transferId}
                </div>
              </DescriptionDetails>
              <DescriptionTerm>Stored status now</DescriptionTerm>
              <DescriptionDetails className="font-medium text-red-700 dark:text-red-400">
                {applyRow.dbStatus}
              </DescriptionDetails>
              <DescriptionTerm>Will become</DescriptionTerm>
              <DescriptionDetails className="font-medium">{applyRow.webhookLogStatus ?? '-'}</DescriptionDetails>
            </DescriptionList>
            {applyStatus.isError && (
              <p
                role="alert"
                className="mt-4 text-sm text-red-600"
              >
                Could not apply the log status. {applyStatus.error?.message} Close this and compare again to see the
                current state.
              </p>
            )}
          </DialogBody>
        )}
        <DialogActions>
          <Button
            plain
            className="disabled:cursor-not-allowed"
            onClick={closeApply}
            disabled={applyStatus.isPending}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            className="disabled:cursor-not-allowed"
            onClick={handleApply}
            disabled={applyStatus.isPending}
          >
            {applyStatus.isPending ? 'Applying…' : 'Apply log status'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
